/**
 * 用户中心 - 我的视频面板
 *
 * 与工作台的 VideoRecentWorks 区别：
 * - 这个有完整分页
 * - 卡片更详细：模型 / 时长 / 分辨率 / 时间 / 状态徽标
 * - 状态过滤器
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Clock,
  Trash2,
  Video as VideoIcon,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { formatBeijingDateTime } from '../../../utils/time';
import {
  videoApi,
  type VideoJobUserView,
  type VideoStatus,
} from '../services/videoApi';
import { VideoLightbox } from './VideoLightbox';
import { withAuthToken } from '../../../utils/storageUrl';

const PAGE_SIZE = 12;

const STATUS_FILTERS: Array<{ value: VideoStatus | 'all'; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'completed', label: '已完成' },
  { value: 'processing', label: '生成中' },
  { value: 'pending', label: '排队中' },
  { value: 'failed', label: '失败' },
  { value: 'timeout', label: '超时' },
];

export function MyVideosPanel() {
  const [items, setItems] = useState<VideoJobUserView[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<VideoStatus | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxJob, setLightboxJob] = useState<{ url: string; cover: string | null } | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await videoApi.listMyJobs({
        page,
        pageSize,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setItems(res.data);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载视频列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // 自动刷新进行中任务（最多 30 秒一次）
  useEffect(() => {
    const hasActive = items.some((j) =>
      ['pending', 'submitting', 'processing'].includes(j.status)
    );
    if (!hasActive) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [items, load]);

  const handleDelete = async (id: string) => {
    if (!confirm('确定隐藏这个视频吗？')) return;
    try {
      await videoApi.deleteJob(id);
      setItems((prev) => prev.filter((j) => j.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handlePlay = async (job: VideoJobUserView) => {
    if (job.status !== 'completed') return;
    try {
      const detail = await videoApi.getJob(job.id);
      const playableUrl = detail.playUrl || detail.resultUrl;
      if (!playableUrl) {
        alert('视频地址不可用');
        return;
      }
      setLightboxJob({
        url: playableUrl,
        cover: detail.coverProxyUrl || detail.coverUrl,
      });
      setLightboxOpen(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : '获取视频失败');
    }
  };

  const handleDownload = async (job: VideoJobUserView) => {
    try {
      const detail = await videoApi.getJob(job.id);
      const downloadUrl = detail.downloadUrl || detail.playUrl || detail.resultUrl;
      if (!downloadUrl) return;
      await downloadFromUrl(downloadUrl, `video-${job.id}.mp4`);
    } catch (err) {
      alert(err instanceof Error ? err.message : '下载失败');
    }
  };

  return (
    <div>
      {/* 头部：标题 + 状态筛选 + 刷新 */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <VideoIcon className="w-5 h-5 text-accent-blue" />
          <h3 className="font-semibold text-light-text">我的视频</h3>
          <span className="text-xs text-light-muted">共 {total} 个</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setStatusFilter(opt.value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-full text-xs transition-all ${
                statusFilter === opt.value
                  ? 'bg-accent-blue text-white'
                  : 'bg-light-hover text-light-secondary hover:bg-light-border'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="p-1 text-light-muted hover:text-accent-blue"
            title="刷新"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* 列表 */}
      {error ? (
        <div className="py-12 text-center text-rose-500 text-sm">{error}</div>
      ) : loading && items.length === 0 ? (
        <div className="py-12 text-center text-light-muted text-sm">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          加载中...
        </div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-light-muted text-sm">
          <VideoIcon className="w-12 h-12 mx-auto mb-2 text-light-border" />
          还没有视频作品
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((job) => (
            <VideoCard
              key={job.id}
              job={job}
              onPlay={() => handlePlay(job)}
              onDelete={() => handleDelete(job.id)}
              onDownload={() => handleDownload(job)}
            />
          ))}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1.5 rounded-lg border border-light-border hover:bg-light-hover disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-light-muted">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="p-1.5 rounded-lg border border-light-border hover:bg-light-hover disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <VideoLightbox
        videoUrl={lightboxJob?.url || null}
        coverUrl={lightboxJob?.cover}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}

interface VideoCardProps {
  job: VideoJobUserView;
  onPlay: () => void;
  onDelete: () => void;
  onDownload: () => void;
}

function VideoCard({ job, onPlay, onDelete, onDownload }: VideoCardProps) {
  const isActive = ['pending', 'submitting', 'processing'].includes(job.status);
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed' || job.status === 'timeout';

  return (
    <div className="card overflow-hidden hover:shadow-xl transition-shadow">
      <div
        className="aspect-video bg-light-hover relative cursor-pointer group"
        onClick={isCompleted ? onPlay : undefined}
      >
        {isActive ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-light-muted">
            <Loader2 className="w-6 h-6 animate-spin text-accent-blue mb-1.5" />
            <span className="text-xs">{getStatusLabel(job.status)}</span>
          </div>
        ) : isFailed ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center text-rose-500">
            <AlertCircle className="w-6 h-6 mb-1.5" />
            <span className="text-xs">{job.status === 'timeout' ? '超时' : '失败'}</span>
            {job.errorMessage && (
              <span className="mt-1 line-clamp-2 max-w-[180px] text-[10px] leading-5 text-rose-500/90">
                {job.errorMessage}
              </span>
            )}
          </div>
        ) : (job.coverProxyUrl || job.coverUrl) ? (
          <>
            <img
              src={withAuthToken(job.coverProxyUrl || job.coverUrl)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
              <span className="px-3 py-1.5 rounded-full bg-white/20 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                ▶ 播放
              </span>
            </div>
          </>
        ) : (job.playUrl || job.resultUrl) ? (
          <>
            <video
              src={withAuthToken(job.playUrl || job.resultUrl) + '#t=0.1'}
              preload="metadata"
              muted
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
              <span className="px-3 py-1.5 rounded-full bg-white/20 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                ▶ 播放
              </span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-black/85 text-white text-xs">
            <span className="px-3 py-1.5 rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
              ▶ 播放
            </span>
          </div>
        )}

        {/* 状态徽标 */}
        <div className="absolute top-2 left-2">
          <StatusBadge status={job.status} />
        </div>

        {/* 操作按钮 */}
        <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {isCompleted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              className="w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
              title="下载"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
            title="隐藏"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-1.5">
        <p className="text-sm text-light-text line-clamp-2 leading-snug">
          {job.prompt || '（无提示词）'}
        </p>
        <div className="flex items-center justify-between text-xs text-light-muted">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            <span>{job.duration}s</span>
            <span>·</span>
            <span>{job.resolution}</span>
            <span>·</span>
            <span>{job.aspectRatio}</span>
          </div>
          <span>{formatTime(job.createdAt)}</span>
        </div>
        <div className="text-[11px] text-light-muted">
          模型：{job.model} · 扣 {job.creditsUsed} 积分
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: VideoJobUserView['status'] }) {
  const config: Record<
    VideoJobUserView['status'],
    { label: string; className: string }
  > = {
    pending: { label: '排队中', className: 'bg-blue-500/85 text-white' },
    submitting: { label: '提交中', className: 'bg-blue-500/85 text-white' },
    processing: { label: '生成中', className: 'bg-amber-500/85 text-white' },
    completed: { label: '已完成', className: 'bg-emerald-500/85 text-white' },
    failed: { label: '失败', className: 'bg-rose-500/85 text-white' },
    timeout: { label: '超时', className: 'bg-zinc-500/85 text-white' },
  };
  const c = config[status];
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return '排队中';
    case 'submitting':
      return '提交中';
    case 'processing':
      return '生成中';
    default:
      return status;
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return '刚刚';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  return formatBeijingDateTime(iso);
}

async function downloadFromUrl(url: string, filename: string): Promise<void> {
  const response = await fetch(withAuthToken(url));
  if (!response.ok) {
    throw new Error(`下载失败: HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

export default MyVideosPanel;
